import type { StoryData } from 'storyblok-js-client';
import { fetchStory } from 'util/storyblok/api';
import ScrollTrigger from 'gsap/dist/ScrollTrigger';
import type { Dispatch, SetStateAction } from 'react';

import { getStoryParams } from '../../pages/[[...slug]].page';

let scriptPromise: Promise<void>;
let scriptResolver: (() => void) | null;

// appends the bridge script tag to our document
// see https://www.storyblok.com/docs/guide/essentials/visual-editor#installing-the-storyblok-js-bridge
export function addBridge(callback: () => void): void {
  // check if the script is already present

  const existingScript = document.querySelector('#storyblokBridge');
  if (!existingScript) {
    scriptPromise = new Promise((resolver) => {
      scriptResolver = resolver;
    });
    const script = document.createElement('script');
    script.src = 'https://app.storyblok.com/f/storyblok-v2-latest.js';
    script.id = 'storyblokBridge';
    document.body.appendChild(script);
    script.onload = () => {
      // once the scrip is loaded, init the event listeners
      if (scriptResolver) {
        scriptResolver();
      }
      callback();
    };
  } else {
    scriptPromise?.then(() => {
      callback();
    });
  }
}

let storyBridgeInitialized = false;

// first load the bridge, then initialize the event listeners
// adds the events for updating the visual editor
// see https://www.storyblok.com/docs/guide/essentials/visual-editor#initializing-the-storyblok-js-bridge
export function initBridgeEventListeners(
  story: StoryData,
  setStory: (story: StoryData) => void,
  setEditorMode: Dispatch<SetStateAction<boolean>>,
  locale?: string,
): void {
  const { StoryblokBridge } = window;
  if (typeof StoryblokBridge !== 'undefined' && !storyBridgeInitialized) {
    storyBridgeInitialized = true;
    const editorLocale =
      new URLSearchParams(window.location.search).get('_storyblok_lang') || locale;
    const storyblokInstance = new StoryblokBridge({
      language: editorLocale,
      resolveRelations: [
        'case_card.case',
        'featured_news_item.news',
        'case.locations',
        'case_listing.locations_filter',
        'locations_blok.quick_links',
        'related_news.news_items',
      ],
    });

    // reload on Next.js page on save event in the Visual Editor
    storyblokInstance.on(['change'], () => {
      window.location.reload();
    });

    // reload on Next.js page on publish event in the Visual Editor
    storyblokInstance.on(['published'], async () => {
      window.location.reload();
    });

    // live update the story on input events
    storyblokInstance.on('input', (event: StoryblokEventPayload) => {
      if (story && event.story?.content?._uid === story.content._uid) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setStory(event.story as StoryData<any>);
        ScrollTrigger.refresh();
      }
    });

    // loading the draft version on initial enter of editor
    storyblokInstance.on('enterEditmode', async (event: StoryblokEventPayload) => {
      try {
        setEditorMode(true);
        const { data } = await fetchStory(
          `${event.storyId}`,
          {
            language: editorLocale,
            ...getStoryParams(story.full_slug[0]),
            version: 'draft',
            cv: Date.now(),
          },
          true,
          true,
        );
        if (data.story) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          setStory(data.story as StoryData<any>);
        }
      } catch (error) {
        console.log(error);
      }
    });
  }
}
